<template>
  <div class="bingo-board">
    <p class="text">
      Wykonaj wszystkie poniższe zadania!
      <br />
      Kliknij w zadanie<span v-if="bingoStore.isFileRequired">
        i wyślij zdjęcie/film</span
      >, gdy zostanie przez Ciebie wykonane.
    </p>
    <div class="playername">
      <p class="playername-input-text">
        Wpisz poniżej swoje imię i nazwisko, aby wygrać nagrodę po wykonaniu
        WSZYSTKICH ZADAŃ i zdobyciu BINGO
      </p>
      <div
        class="playername-input-wrapper"
        :class="{ 'playername-input-wrapper-error': isErrorInput }"
      >
        <input
          ref="isErrorInputRef"
          v-model="playerName"
          class="playername-input"
          :class="{ 'playername-input-error': isErrorInput }"
          placeholder="Twoje imie i nazwisko"
          @blur="updatePlayerName"
        />
      </div>
      <div v-if="bingoStore.isScoreboard" class="scoreboard">
        <button class="scoreboard-button" @click="showScoreboard">
          Tablica wyników
        </button>
      </div>
    </div>
    <table class="bingo-table">
      <tr
        v-for="(row, rowIndex) in bingoCards"
        :key="rowIndex"
        class="bingo-table-column"
      >
        <td
          v-for="(card, cardIndex) in row"
          :key="cardIndex"
          class="bingo-table-row"
        >
          <BingoCard :card="card" @toggle-error="updateError" />
        </td>
      </tr>
    </table>
    <button class="reset-button" @click="confirmResetGame()">
      Resetuj gre
    </button>
    <transition name="fade" mode="out-in">
      <BingoWin v-if="bingoStore.isEndGame && isBingo" text="BINGO!" />
      <!-- <BingoWin v-else-if="isBingo" text="BINGO!" /> -->
    </transition>
    <ConfirmDialog
      group="bingo-board-dialog"
      :closable="false"
      :style="dialogColors"
      :breakpoints="{ '600px': '80vw', '400px': '95vw', '300px': '100vw' }"
    >
      <template #container="{ message, acceptCallback, rejectCallback }">
        <div class="game-reset-dialog">
          <span class="game-reset-dialog-header">{{ message.header }}</span>
          <p class="game-reset-dialog-message">{{ message.message }}</p>
          <div class="game-reset-dialog-buttons">
            <button
              class="game-reset-dialog-buttons-common game-reset-dialog-buttons-reject"
              @click="rejectCallback"
            >
              Nie
            </button>
            <button
              class="game-reset-dialog-buttons-common game-reset-dialog-buttons-accept"
              @click="acceptCallback"
            >
              Tak, zresetuj
            </button>
          </div>
        </div>
      </template>
    </ConfirmDialog>
    <DynamicDialog />
  </div>
</template>

<script setup lang="ts">
import BingoCard from './BingoCard.vue';
import { ref, toRef, computed, watch, nextTick } from 'vue';
import { useStateStore } from '@/stores/state.store';
import { useBingoStore } from '@/stores/bingo/bingo.store';
import { Card } from '@/stores/bingo/bingo.state';
import BingoWin from './BingoWin.vue';
import { useConfirm } from 'primevue/useconfirm';
import BingoScoreboardDialog from './BingoScoreboardDialog.vue';
import { useDialog } from 'primevue/usedialog';

const dialog = useDialog();
const confirm = useConfirm();

const stateStore = useStateStore();
const bingoStore = useBingoStore();

const isBingo = ref(false);
const playerName = toRef(bingoStore.playerName);
const isErrorInput = ref(false);
const isErrorInputRef = ref<HTMLInputElement | null>(null);

bingoStore.loadBingoCards();

const bingoCards = computed(() => {
  const cards = bingoStore.getBingoCards;
  const rows: Array<Array<Card>> = [];
  for (let i = 0; i < cards.length; i += 3) {
    rows.push(cards.slice(i, i + 3));
  }
  return rows;
});

const refreshCards = () => {
  bingoStore.refreshBingoCards();
};

const updatePlayerName = () => {
  if (playerName.value !== '') {
    bingoStore.setPlayerName(playerName.value);
    isErrorInput.value = false;
  } else {
    playerName.value = bingoStore.playerName;
  }
};

const updateError = () => {
  if (playerName.value === '') {
    isErrorInput.value = true;
    isErrorInputRef.value?.focus();
  }
};

watch(playerName, () => {
  isErrorInput.value = false;
});

watch(isBingo, (val) => {
  if (val) {
    setTimeout(() => {
      isBingo.value = false;
    }, 5000);
  }
});

watch(
  () => bingoStore.bingoCount,
  (val, oldVal) => {
    if (val > oldVal) {
      nextTick(() => {
        isBingo.value = true;
      });
    }
  },
);

const backgroundColor = ref(stateStore.colors.backgroundColor);
const mainColor = ref(stateStore.colors.mainColor);

const confirmResetGame = (): void => {
  confirm.require({
    header: 'Czy na pewno chcesz zresetować grę?',
    message: 'Wszystkie twoje postępy zostaną usunięte',
    group: 'bingo-board-dialog',
    accept: () => refreshCards(),
    reject: () => {},
    defaultFocus: 'reject',
  });
};

const dialogColors = {
  '--main-color': mainColor.value,
  '--background-color': backgroundColor.value,
  '--border-color': mainColor.value,
  '--p-dialog-border-color': mainColor.value,
  '--p-dialog-background': backgroundColor.value,
};

const showScoreboard = () => {
  dialog.open(BingoScoreboardDialog, {
    props: {
      header: 'Tablica wyników',
      style: {
        color: mainColor.value,
        background: backgroundColor.value,
        'border-color': mainColor.value,
        width: '50vw',
        '--p-button-text-secondary-color': mainColor.value,
        '--p-button-text-secondary-hover-background': backgroundColor.value,
        '--p-button-text-secondary-active-background': backgroundColor.value,
        '--p-button-primary-border-color': mainColor.value,
        '--p-button-primary-hover-border-color': mainColor.value,
        '--p-button-primary-active-border-color': mainColor.value,
        '--p-button-primary-hover-background': backgroundColor.value,
        '--p-button-primary-active-background': backgroundColor.value,
        '--p-button-secondary-border-color': mainColor.value,
        '--p-button-secondary-hover-border-color': mainColor.value,
        '--p-button-secondary-active-border-color': mainColor.value,
        '--p-button-secondary-hover-background': backgroundColor.value,
        '--p-button-secondary-active-background': backgroundColor.value,
        '--p-button-secondary-background': backgroundColor.value,
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw',
      },
      modal: true,
      draggable: false,
    },
    data: {
      mainColor: mainColor.value,
      buttonColor: mainColor.value,
      backgroundColor: backgroundColor.value,
    },
  });
};
</script>

<style scoped lang="scss">
@import '@/assets/styleConstans';

.bingo-board {
  display: flex;
  flex-direction: column;
  gap: 15px;
  max-width: 800px;
  margin: auto;
  padding: 20px;
  text-align: center;
  justify-content: center;
  align-items: center;
}

.text {
  font-size: 1.4em;
  color: v-bind(mainColor);

  @media (max-width: $media-phone) {
    font-size: 1.1em;
  }
}

.bingo-table {
  width: 100%;
  margin-bottom: 20px;
  table-layout: fixed;
  height: 100%;

  .bingo-table-column {
    position: relative;
    flex-direction: row;
    gap: 15px;
  }

  .bingo-table-row {
    position: relative;
    height: 100%;
    padding: 5px;
  }
}

.reset-button {
  width: 250px;
  border-radius: 48.707px;
  border: 2px solid;
  padding: 10px 20px;
  font-size: 2em;
  white-space: nowrap;
  text-align: center;
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
  background-color: v-bind(backgroundColor);
  color: v-bind(mainColor);
  cursor: pointer;

  @media (max-width: $media-phone) {
    font-size: 1.5em;
    padding: 5px;
    width: 180px;
  }

  &:hover {
    background-color: v-bind(mainColor);
    color: v-bind(backgroundColor);
  }
}

.playername {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  align-items: center;

  &-input {
    border: 1px solid v-bind(mainColor);
    border-radius: 10px;
    font-size: 1.25em;
    padding: 10px 15px;
    width: 100%;
    color: v-bind(mainColor);
    background-color: v-bind(backgroundColor);

    &:focus-visible {
      outline: 1px solid v-bind(mainColor);
    }

    &::placeholder {
      color: v-bind(mainColor);
      opacity: 0.6;
    }

    @media (max-width: $media-phone) {
      font-size: 1.1em;
    }

    &-wrapper {
      position: relative;
      width: 80%;

      @media (max-width: $media-phone) {
        width: 75%;
      }

      &-error {
        &::after {
          position: absolute;
          right: 7px;
          top: calc(50% - 15px);
          width: 30px;
          height: 30px;
          display: flex;
          justify-content: center;
          align-items: center;
          color: v-bind(mainColor);
          font-size: 1.9em;
          border-radius: 50%;
          background-color: #f00;
          content: '!';
        }
      }
    }

    &-text {
      color: v-bind(mainColor);
    }

    &-error {
      outline: 2px solid #f00;
      padding-right: 50px;

      @media (max-width: $media-phone) {
        padding-right: 30px;
      }

      &:focus-visible {
        outline: 2px solid #f00;
      }
    }
  }
}

.game-reset-dialog {
  display: flex;
  flex-direction: column;
  padding: 10px 15px;
  gap: 15px;
  justify-content: center;
  align-items: center;
  color: var(--main-color);
  background-color: var(--background-color);
  border-radius: 12px;

  &-header {
    font-weight: 700;
    font-size: 1.5em;
    line-height: 1.25em;
    text-align: center;
  }

  &-message {
    font-size: 1em;
    text-align: center;
  }

  &-buttons {
    display: flex;
    flex-direction: row;
    gap: 7px;
    justify-content: space-between;
    margin: auto;
    width: 70%;

    @media (max-width: $media-phone) {
      width: 75%;
    }

    &-common {
      border: 1px solid var(--main-color);
      border-radius: 10px;
      padding: 5px 15px;
      cursor: pointer;
      font-size: 1.2em;
      transition: all ease 0.7s;
    }

    &-accept {
      color: var(--main-color);
      background-color: var(--background-color);

      &:hover {
        color: var(--background-color);
        background-color: var(--main-color);
      }
    }

    &-reject {
      color: var(--background-color);
      background-color: var(--main-color);

      &:hover {
        color: var(--main-color);
        background-color: var(--background-color);
      }
    }
  }
}

.scoreboard {
  &-button {
    color: v-bind(mainColor);
    font-size: 1.8em;
    border: 1px solid v-bind(mainColor);
    border-radius: 10px;
    padding: 5px 10px;
    cursor: pointer;
    transition: ease all 0.7s;

    &:hover {
      color: v-bind(backgroundColor);
      background-color: v-bind(mainColor);
    }

    @media (max-width: $media-phone) {
      font-size: 1.4em;
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1.5s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
